import axios, { isAxiosError } from 'axios'
import dayjs from 'dayjs'
import {
  ChangeEvent,
  ChangeEventHandler,
  FC,
  VFC,
  memo,
  useCallback,
  useEffect,
  useState
} from 'react'
import { unstable_batchedUpdates } from 'react-dom'
import { useSelector } from 'react-redux'
import auth from '../../Services/auth'
import etrBackend from '../../api/etrBackend'
import { BASE_API_URL } from '../../config/pathApi'
import { ETR_STATUS, ETR_STATUS_THAI, server } from '../../constants'
import { MapReceiptType } from '../../constants/paymentDetail'
import { validateFileSize } from '../../helpers/file'
import { getUploadType } from '../../helpers/receipt'
import { isBranchStaff } from '../../helpers/roles'
import { RootReducers } from '../../redux/reducers'
import { WatDatePicker } from '../datePicker'
import classes from './style.module.css'

enum DOCUMENT_TYPE {
  USE_EXISTING = 'USE_EXISTING',
  NEW_DOCUMENT = 'NEW_DOCUMENT'
}

interface UploadPaymentReceiptProp {
  paymentMethodId: number
  receiptNo: string
  onSuccess?: () => void
  documentId: number
  documentDetailId: number
  receipts: ReceiptModel[]
}

interface ViewUploadFileButtonProp {
  fileName: string
  fileType: string
}

interface ExistingReceiptListProp {
  existingReceipt: ReceiptModel[]
  handleSelectReceipt: (value: number) => void
  selectReceipt: number[]
  isFetchReceipt: boolean
  receipts: ReceiptModel[]
  handleSelectUseExistingDocument: ChangeEventHandler
  documentRef: string
  documents: any[]
  isFetchDocumentSuccess: boolean
  documentType: string
}

interface UploadFileProp {
  files: {
    file: File | null
    name: string
    require: boolean
  }[]
  handleFileChange: (e: ChangeEvent<HTMLInputElement>, index: number) => void
}

interface ExistingInputProp {
  documentType: string
  handleDocumentRefChange: ChangeEventHandler
  disabled: boolean
  setDateFilter: (value: string) => void
  dateFilter: string
  documentStatus: string
  setDocumentStatus: (value: string) => void
  fetchDocuments: () => void
}

type NewDocumentInputProp = Pick<ExistingInputProp, 'handleDocumentRefChange' | 'documentType'> & {
  disabled: boolean
}

export const ViewUploadFileButton: VFC<ViewUploadFileButtonProp> = memo(
  function ViewUploadFileButton({ fileName, fileType }) {
    const [fetching, setFetching] = useState(false)

    const viewUploadFile = () => {
      if (fetching) {
        return
      }

      setFetching(true)
      axios
        .get(BASE_API_URL + server.RECEIPT + '/upload/' + btoa(fileName), {
          headers: {
            'api-key': window._env_.API_KEY,
            Authorization: `Bearer ${auth.accessToken()}`
          },
          responseType: 'blob'
        })
        .then(res => {
          window.open(window.URL.createObjectURL(res.data), '_blank')
        })
        .finally(() => {
          setFetching(false)
        })
    }

    return (
      <button
        className={classes.btn_view_receipt}
        onClick={viewUploadFile}
        disabled={fetching}
        style={{ opacity: fetching ? 0.8 : 1 }}
      >
        {MapReceiptType[fileType as keyof typeof MapReceiptType]}
      </button>
    )
  }
)

const ExistingReceiptList: VFC<ExistingReceiptListProp> = memo(function ExistingReceiptList({
  existingReceipt,
  handleSelectReceipt,
  selectReceipt,
  isFetchReceipt,
  receipts = [],
  handleSelectUseExistingDocument,
  documentRef,
  documents,
  isFetchDocumentSuccess,
  documentType
}) {
  const filterExistingReceipt = [...existingReceipt].filter(
    item => !receipts.find(r => r.fileType === item.fileType)
  )

  return (
    <>
      {documents.length > 0 && documentType === DOCUMENT_TYPE.USE_EXISTING && (
        <div className={classes.document_input_wrapper}>
          <div style={{ marginBottom: 5 }}>เลขที่ใบรับเงิน</div>
          <select
            className={`${classes.select} field-input`}
            name="document_id"
            id="document_id"
            disabled={documentType !== DOCUMENT_TYPE.USE_EXISTING}
            value={documentType === DOCUMENT_TYPE.USE_EXISTING ? documentRef : ''}
            onChange={handleSelectUseExistingDocument}
          >
            <option value="">เลือก</option>
            {documents.map(item => (
              <option value={item.id} key={item.receiptNo}>
                {item.receiptNo}
              </option>
            ))}
          </select>

          {documentType === DOCUMENT_TYPE.USE_EXISTING &&
            isFetchDocumentSuccess &&
            documents.length <= 0 && (
              <div className={classes.no_document}>ไม่มีข้อมูลเอกสารรับเงินชั่วคราว</div>
            )}

          <div className={classes.existing_receipt_list}>
            {isFetchReceipt && <div className={classes.loading_receipt_text}>กำลังโหลดข้อมูล</div>}

            {!isFetchReceipt && documentRef && filterExistingReceipt.length <= 0 && (
              <div className={classes.no_receipt_text}>ไม่มีข้อมูลการอัปโหลด</div>
            )}

            {documentRef &&
              filterExistingReceipt.map(item => (
                <div key={item.id} className={classes.existing_receipt}>
                  <input
                    type="checkbox"
                    id={`receipt_${item.id}`}
                    name={item.fileType + '_' + item.id}
                    onChange={() => handleSelectReceipt(item.id)}
                    checked={selectReceipt.includes(item.id)}
                  />
                  <ViewUploadFileButton fileName={item.fileName} fileType={item.fileType} />
                </div>
              ))}
          </div>
        </div>
      )}
    </>
  )
})

const UploadFile: VFC<UploadFileProp> = memo(function UploadFile({ files, handleFileChange }) {
  return (
    <div className={classes.upload_wrapper}>
      {files.map((file, index) => {
        const fileId = `upload_file_${file.name}`
        return (
          <div key={file.name} className={classes.upload_item}>
            <div>
              {MapReceiptType[file.name as keyof typeof MapReceiptType]}{' '}
              {file.require && <span className={classes.requireField}>*</span>}
            </div>
            <label htmlFor={fileId} className={classes.upload_btn}>
              <div>{file.file?.name ?? 'เลือกไฟล์'}</div>
              <input
                type="file"
                id={fileId}
                className={classes.input_file}
                onChange={e => handleFileChange(e, index)}
                accept="image/jpeg,application/pdf"
              />
            </label>
          </div>
        )
      })}
    </div>
  )
})

const ExistingInput: FC<ExistingInputProp> = memo(function ExistingInput({
  documentType,
  handleDocumentRefChange,
  disabled,
  setDateFilter,
  dateFilter,
  documentStatus,
  setDocumentStatus,
  fetchDocuments
}) {
  return (
    <div className={classes.input_group}>
      <div className={classes.label}>
        <input
          type="radio"
          name="DOCUMENT_TYPE"
          className={classes.document_ref}
          value={DOCUMENT_TYPE.USE_EXISTING}
          checked={documentType === DOCUMENT_TYPE.USE_EXISTING}
          onChange={handleDocumentRefChange}
          id="DOCUMENT_TYPE_1"
          disabled={disabled}
        />
        <label className={classes.label_text} htmlFor="DOCUMENT_TYPE_1">
          ใช้เอกสารร่วมกับ etr อื่น
        </label>
      </div>
      <div className={classes.input_filter}>
        <div>
          <div>วันที่</div>
          <WatDatePicker
            name="receiptdate"
            dateFormat={'yyyy-MM-dd'}
            displayFormat={'DD/MM/YYYY'}
            clearable={false}
            minDate={dayjs().add(-2, 'year')}
            maxDate={dayjs().add(5, 'year')}
            disabled={documentType !== DOCUMENT_TYPE.USE_EXISTING}
            readOnly={false}
            yearBoundary={99}
            onChange={(chrisDate: any, buddhistDate: any) => {
              // console.log(chrisDate)
              setDateFilter(chrisDate)
            }}
            value={dateFilter}
          />
        </div>
        <div>
          <div>สถานะ</div>
          <select
            className={`${classes.select} field-input`}
            name="document_status"
            id="document_status"
            disabled={documentType !== DOCUMENT_TYPE.USE_EXISTING}
            value={documentStatus}
            onChange={e => setDocumentStatus(e.target.value)}
          >
            <option value={ETR_STATUS.NEW_ETR}>{ETR_STATUS_THAI[0].Desc}</option>
            <option value={ETR_STATUS.SUBMITED_ETR}>{ETR_STATUS_THAI[4].Desc}</option>
          </select>
        </div>
        <button
          className={classes.btn_search}
          disabled={documentType !== DOCUMENT_TYPE.USE_EXISTING}
          onClick={fetchDocuments}
        >
          ค้นหา
        </button>
      </div>
    </div>
  )
})

const NewDocumentInput: FC<NewDocumentInputProp> = memo(function NewDocumentInput({
  documentType,
  handleDocumentRefChange,
  disabled = false
}) {
  return (
    <div className={classes.inputUploadFile}>
      <div className={classes.label}>
        <input
          type="radio"
          name="DOCUMENT_TYPE"
          className={classes.document_ref}
          value={DOCUMENT_TYPE.NEW_DOCUMENT}
          checked={documentType === DOCUMENT_TYPE.NEW_DOCUMENT}
          onChange={handleDocumentRefChange}
          id="DOCUMENT_TYPE_2"
          disabled={disabled}
        />
        <label className={classes.label_text} htmlFor="DOCUMENT_TYPE_2">
          อัปโหลดเอกสารใหม่
        </label>
      </div>
    </div>
  )
})

const UploadPaymentReceipt: VFC<UploadPaymentReceiptProp> = ({
  documentId,
  paymentMethodId,
  documentDetailId,
  receiptNo,
  receipts,
  onSuccess
}) => {
  const loginState = useSelector((state: RootReducers) => state.loginReducer)
  const agentId = loginState.account?.agentInfo.agentId
  const isAO = loginState.account?.agentInfo.isAO
  const isBranch = isBranchStaff(loginState.account?.agentInfo.role as string)
  const [dateFilter, setDateFilter] = useState(dayjs().format('YYYY-MM-DD'))
  const [files, setFiles] = useState(getUploadType(paymentMethodId, receipts, isBranch))
  const [error, setError] = useState(
    files.length > 0 ? '' : 'ไม่สามารถอัปโหลดไฟล์เพิ่มเติมได้เนื่องจากคุณได้อัปโหลดไฟล์ครบหมดแล้ว'
  )
  const [documents, setDocuments] = useState<any[]>([])
  const [documentType, setDocumentType] = useState('')
  const [existingReceipt, setExistingReceipt] = useState<any[]>([])
  const [isFetchReceipt, setIsFetchReceipt] = useState(false)
  const [isFetchDocumentSuccess, setIsFetchDocumentSuccess] = useState(false)
  const [documentRef, setDocumentRef] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [selectReceipt, setSelectReceipt] = useState<number[]>([])
  const [documentStatus, setDocumentStatus] = useState(ETR_STATUS.NEW_ETR)
  const isDisableAllInput = files.length <= 0

  const handleDocumentRefChange: ChangeEventHandler<HTMLInputElement> = e => {
    unstable_batchedUpdates(() => {
      setDocumentType(e.target.value)
      setExistingReceipt([])
      setDocumentRef('')
      setError('')
      setDocuments([])
      setIsFetchDocumentSuccess(false)
    })
  }

  const validateFileType = (type: string) => {
    return ['application/pdf', 'image/jpeg'].includes(type)
  }

  const handleFileChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>, index: number) => {
      const file = e.target.files?.[0] || null

      if (file) {
        if (!validateFileType(file.type)) {
          setError(`Not allowed file type ${file.type}`)
          files[index].file = null
          return
        }

        if (!validateFileSize(file.size)) {
          setError(`Maximum file size is 5MB`)
          files[index].file = null
          return
        }
      }

      files[index].file = file

      setFiles([...files])
      setError('')
    },
    [files]
  )

  const fetchDocuments = () => {
    const search = `?pageSize=50&page=1&status=${documentStatus}&keyword=${dateFilter}`

    let apiUrl = server.TRANSACTION_URL + `/agent/${agentId}`
    if (window._env_.NODE_ENV === 'local') {
      apiUrl = `${server.TRANSACTION_URL}`
    }
    if (isAO) {
      apiUrl = apiUrl + '/AO'
    }
    if (isBranch) {
      apiUrl = server.TRANSACTION_URL + `/pagination`
    }

    setDocumentRef('')
    setDocuments([])
    setExistingReceipt([])
    setIsFetchDocumentSuccess(false)

    etrBackend
      .get(apiUrl + search)
      .then(res => {
        const data = res.data.data
        if (Array.isArray(data) && documentId) {
          setDocuments(data.filter(item => item.id !== documentId))
        }
      })
      .catch(err => {
        if (isAxiosError(err)) {
          const message = err.response?.data?.message

          setError(message || `Error status code ${err.response?.status}`)
        } else {
          setError(err.message)
        }
      })
      .finally(() => {
        setIsFetchDocumentSuccess(true)
      })
  }

  const getAllReceipt = useCallback(
    (value: string) => {
      if (documentType !== DOCUMENT_TYPE.USE_EXISTING || value === '') {
        return
      }

      setExistingReceipt([])
      setIsFetchReceipt(true)

      etrBackend
        .get<ReceiptModel[]>(server.RECEIPT + '/' + value)
        .then(res => {
          setExistingReceipt(res.data.filter(item => item.paymentMethodId === paymentMethodId))
        })
        .finally(() => {
          setIsFetchReceipt(false)
        })
    },
    [documentType, paymentMethodId]
  )

  const handleSelectUseExistingDocument: ChangeEventHandler<HTMLSelectElement> = useCallback(
    e => {
      setDocumentRef(e.target.value)
      getAllReceipt(e.target.value)
    },
    [getAllReceipt]
  )

  const validateData = () => {
    if (documentType === DOCUMENT_TYPE.NEW_DOCUMENT) {
      return (
        documentId &&
        files.filter(item => item.require && item.file === null).length <= 0 &&
        receiptNo
      )
    } else {
      return documentRef && selectReceipt.length > 0 && receiptNo
    }
  }

  const handleOnSubmit = () => {
    if (documentType === DOCUMENT_TYPE.NEW_DOCUMENT) {
      uploadReceipt()
    } else {
      saveExistingReceipt()
    }
  }

  const saveExistingReceipt = () => {
    if (!validateData()) {
      return
    }

    setSubmitting(true)

    const reqBody = {
      documentId: documentId,
      documentDetailId: documentDetailId,
      documentRef: parseInt(documentRef),
      paymentMethodId: paymentMethodId,
      receipts: selectReceipt,
      receiptNo: receiptNo
    }

    etrBackend
      .post(server.RECEIPT + '/' + 'reuseExistingReceipt', reqBody)
      .then(() => {
        onSuccess?.()
        setError('')
      })
      .catch(err => {
        if (isAxiosError(err)) {
          const message = err.response?.data?.message

          setError(message || `Error status code ${err.response?.status}`)
        } else {
          setError(err.message)
        }
      })
      .finally(() => {
        setSubmitting(false)
      })
  }

  const uploadReceipt = () => {
    if (!validateData()) {
      return
    }

    setSubmitting(true)

    const formData = new FormData()

    formData.append('receipt_no', receiptNo)

    files.forEach(_file => {
      if (_file.file !== null) {
        formData.append(_file.name.toLowerCase(), _file.file)
      }
    })

    if (typeof documentId === 'number') {
      formData.append('documentId', documentId.toString())
    }

    if (typeof documentDetailId === 'number') {
      formData.append('documentDetailId', documentDetailId.toString())
    }

    if (typeof paymentMethodId === 'number') {
      formData.append('paymentMethodId', paymentMethodId.toString())
    }

    axios
      .post(BASE_API_URL + server.RECEIPT, formData, {
        headers: {
          'api-key': window._env_.API_KEY,
          Authorization: `Bearer ${auth.accessToken()}`
        }
      })
      .then(() => {
        onSuccess?.()
        setError('')
      })
      .catch(err => {
        if (isAxiosError(err)) {
          const message = err.response?.data?.message

          setError(message || `Error status code ${err.response?.status}`)
        } else {
          setError(err.message)
        }
      })
      .finally(() => {
        setSubmitting(false)
      })
  }

  const handleSelectReceipt = useCallback(
    (value: number) => {
      let temp = selectReceipt
      if (temp.includes(value)) {
        temp = temp.filter(item => item !== value)
      } else {
        temp.push(value)
      }

      setSelectReceipt([...temp])
    },
    [selectReceipt]
  )

  useEffect(() => {
    setFiles(getUploadType(paymentMethodId, receipts, isBranch))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentMethodId, receipts, isBranch])

  return (
    <div className={classes.wrapper}>
      {submitting && <div className={classes.overlay} />}

      <ExistingInput
        documentType={documentType}
        handleDocumentRefChange={handleDocumentRefChange}
        disabled={isDisableAllInput}
        setDateFilter={setDateFilter}
        dateFilter={dateFilter}
        setDocumentStatus={setDocumentStatus}
        documentStatus={documentStatus}
        fetchDocuments={fetchDocuments}
      />

      <ExistingReceiptList
        documentRef={documentRef}
        documents={documents}
        existingReceipt={existingReceipt}
        handleSelectReceipt={handleSelectReceipt}
        selectReceipt={selectReceipt}
        isFetchReceipt={isFetchReceipt}
        receipts={receipts}
        handleSelectUseExistingDocument={handleSelectUseExistingDocument}
        isFetchDocumentSuccess={isFetchDocumentSuccess}
        documentType={documentType}
      />

      <NewDocumentInput
        documentType={documentType}
        handleDocumentRefChange={handleDocumentRefChange}
        disabled={isDisableAllInput}
      />

      {documentType === DOCUMENT_TYPE.NEW_DOCUMENT && (
        <UploadFile handleFileChange={handleFileChange} files={files} />
      )}

      <div className={classes.action}>
        <span className={classes.errorMessage}>{error !== '' && error}</span>
        <button
          className={classes.btn_submit}
          onClick={handleOnSubmit}
          disabled={!validateData() || submitting}
        >
          {submitting ? 'กำลังบันทึกข้อมูล...' : 'ตกลง'}
        </button>
      </div>
    </div>
  )
}

export default UploadPaymentReceipt
